import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getSurveys } from '../../services/adminService';
import { SubmittedSurvey, SurveyFilters, PaginatedResponse } from '../../types/admin';
import './SurveysList.css';
import { getRiskLevelClass, getRiskLevelText } from '../../utils/surveyUtils';

export const SurveysList: React.FC = () => {
	const { token } = useAuth();
	const [surveys, setSurveys] = useState<PaginatedResponse<SubmittedSurvey> | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>('');
	const [filters, setFilters] = useState<SurveyFilters>({});
	const [currentPage, setCurrentPage] = useState(1);
	const pageSize = 20;

	const loadSurveys = useCallback(
		async (page: number = 1, newFilters?: SurveyFilters) => {
			if (!token) return;

			try {
				setLoading(true);
				setError('');
				const data = await getSurveys(token, page, pageSize, newFilters || filters);
				setSurveys(data);
			} catch (err) {
				setError('Greška pri učitavanju upitnika');
				console.error('Surveys list error:', err);
			} finally {
				setLoading(false);
			}
		},
		[token, filters]
	);

	useEffect(() => {
		loadSurveys(currentPage);
	}, [loadSurveys, currentPage]);

	const handleFilterChange = (field: keyof SurveyFilters, value: string) => {
		const newFilters = { ...filters, [field]: value || undefined };
		setFilters(newFilters);
		setCurrentPage(1);
		loadSurveys(1, newFilters);
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		loadSurveys(page);
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('sr-RS', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const clearFilters = () => {
		setFilters({});
		setCurrentPage(1);
		loadSurveys(1, {});
	};

	if (loading && !surveys) {
		return (
			<div className='surveys-list-loading'>
				<p>Učitavanje upitnika...</p>
			</div>
		);
	}

	if (error && !surveys) {
		return (
			<div className='surveys-list-error'>
				<p>{error}</p>
				<button onClick={() => loadSurveys()} className='btn btn-primary'>
					Pokušaj ponovo
				</button>
			</div>
		);
	}

	return (
		<div className='surveys-list'>
			<div className='surveys-header'>
				<h1>Upravljanje upitnicima</h1>
				<p>Pregled i analiza svih popunjenih upitnika</p>
			</div>

			{/* Filters */}
			<div className='surveys-filters'>
				<h3>Filteri</h3>
				<div className='filters-grid'>
					<div className='filter-group'>
						<label htmlFor='startDate'>Od datuma:</label>
						<input type='date' id='startDate' value={filters.startDate || ''} onChange={(e) => handleFilterChange('startDate', e.target.value)} />
					</div>

					<div className='filter-group'>
						<label htmlFor='endDate'>Do datuma:</label>
						<input type='date' id='endDate' value={filters.endDate || ''} onChange={(e) => handleFilterChange('endDate', e.target.value)} />
					</div>

					<div className='filter-group'>
						<label htmlFor='targetGroup'>Ciljna grupa:</label>
						<select id='targetGroup' value={filters.targetGroup || ''} onChange={(e) => handleFilterChange('targetGroup', e.target.value)}>
							<option value=''>Sve</option>
							<option value='kids'>Deca</option>
							<option value='adults'>Odrasli</option>
							<option value='health-workers'>Zdravstveni radnici</option>
						</select>
					</div>

					<div className='filter-group'>
						<label htmlFor='riskLevel'>Nivo rizika:</label>
						<select id='riskLevel' value={filters.riskLevel || ''} onChange={(e) => handleFilterChange('riskLevel', e.target.value)}>
							<option value=''>Svi</option>
							<option value='high'>Visok</option>
							<option value='medium'>Srednji</option>
							<option value='low'>Nizak</option>
						</select>
					</div>

					<div className='filter-group'>
						<label htmlFor='hasReport'>Ima prijavu:</label>
						<select id='hasReport' value={filters.hasReport?.toString() || ''} onChange={(e) => handleFilterChange('hasReport', e.target.value)}>
							<option value=''>Sve</option>
							<option value='true'>Da</option>
							<option value='false'>Ne</option>
						</select>
					</div>
				</div>

				<div className='filters-actions'>
					<button onClick={clearFilters} className='btn user-btn-secondary'>
						Obriši filtere
					</button>
					<button onClick={() => loadSurveys(currentPage)} className='btn btn-primary' disabled={loading}>
						{loading ? 'Učitava...' : 'Osveži'}
					</button>
				</div>
			</div>

			{/* Results */}
			{surveys && (
				<div className='surveys-results'>
					<div className='results-header'>
						<h3>Rezultati ({surveys.total} upitnika)</h3>
						<div className='results-info'>
							Strana {surveys.page} od {surveys.totalPages}
						</div>
					</div>

					{surveys.data.length === 0 ? (
						<div className='no-surveys'>
							<p>Nema upitnika koji odgovaraju filterima</p>
						</div>
					) : (
						<div className='surveys-table'>
							<div className='table-header'>
								<div className='col-date'>Datum</div>
								<div className='col-group'>Grupa</div>
								<div className='col-age'>Godine</div>
								<div className='col-risk'>Rizik</div>
								<div className='col-report'>Prijava</div>
								<div className='col-actions'>Akcije</div>
							</div>

							<div className='table-body'>
								{surveys.data.map((survey) => (
									<div key={survey.id} className='table-row'>
										<div className='col-date'>{formatDate(survey.submittedAt)}</div>
										<div className='col-group'>{survey.targetGroup === 'kids' ? 'Deca' : 'Odrasli'}</div>
										<div className='col-age'>{!!survey.age || 'N/A'}</div>
										<div className='col-risk'>
											<span className={`risk-badge ${getRiskLevelClass(survey.riskLevel)}`}>{getRiskLevelText(survey.riskLevel)}</span>
										</div>

										<div className='col-report'>{survey.report ? <span className='yes-report'>Podneta</span> : <span className='no-report'>Nema</span>}</div>
										<div className='col-actions'>
											<Link to={`/admin/surveys/${survey.id}`} className='btn btn-primary btn-sm'>
												Prikaži
											</Link>
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Pagination */}
					{surveys.totalPages > 1 && (
						<div className='pagination'>
							<button onClick={() => handlePageChange(surveys.page - 1)} disabled={surveys.page <= 1} className='btn btn-outline btn-sm'>
								← Prethodna
							</button>

							<div className='page-numbers'>
								{Array.from({ length: Math.min(5, surveys.totalPages) }, (_, i) => {
									const page = Math.max(1, surveys.page - 2) + i;
									if (page > surveys.totalPages) return null;

									return (
										<button key={page} onClick={() => handlePageChange(page)} className={`btn btn-sm ${page === surveys.page ? 'btn-primary' : 'btn-outline'}`}>
											{page}
										</button>
									);
								})}
							</div>

							<button onClick={() => handlePageChange(surveys.page + 1)} disabled={surveys.page >= surveys.totalPages} className='btn btn-outline btn-sm'>
								Sledeća →
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
